'use client';

import { useEffect, useLayoutEffect, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';

type GsapSmoothScrollerProps = {
  children: ReactNode;
};

const ANCHOR_SCROLL_EXTRA_OFFSET_FALLBACK = 16;
const ANCHOR_SCROLL_MIN_DURATION = 0.7;
const ANCHOR_SCROLL_MAX_DURATION = 2.4;
const ANCHOR_SCROLL_PX_PER_SECOND = 1200;
const ANCHOR_TARGET_RETRY_DELAY = 50;
const ANCHOR_TARGET_MAX_RETRIES = 60;
const ANCHOR_ALIGN_TOLERANCE_PX = 12;
const ANCHOR_SCROLL_REQUEST_EVENT = 'wcl:anchor-scroll-requested';
/** Ignore tiny finger jitter on iOS so a tap doesn't cancel its own scroll tween. */
const TOUCH_SCROLL_INTERRUPT_THRESHOLD_PX = 10;
/** After starting an anchor tween, ignore touch briefly while the tap settles. */
const TOUCH_INTERRUPT_GRACE_MS = 450;

let activeAnchorScrollFrame: number | null = null;
let pendingHashScrollTimer: number | null = null;
let activeRouteHash: string | null = null;
let suppressHashChangeScroll = false;
let anchorScrollInProgress = false;
let touchInterruptGraceUntil = 0;
let touchInterruptStartY: number | null = null;

const getCssPixelValue = (name: string, fallback = 0) => {
  const cssValue = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const parsedValue = Number.parseFloat(cssValue);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getHeaderOffset = () => {
  const header = document.getElementById('site-header');

  if (header) {
    return header.getBoundingClientRect().height;
  }

  return getCssPixelValue('--site-header-height');
};

export const getAnchorScrollOffset = () => {
  return (
    getHeaderOffset() +
    getCssPixelValue('--anchor-scroll-extra-offset', ANCHOR_SCROLL_EXTRA_OFFSET_FALLBACK)
  );
};

const getAnchorScrollDuration = (from: number, to: number) => {
  const distance = Math.abs(to - from);
  const duration = distance / ANCHOR_SCROLL_PX_PER_SECOND;

  return Math.min(ANCHOR_SCROLL_MAX_DURATION, Math.max(ANCHOR_SCROLL_MIN_DURATION, duration));
};

const cancelPendingHashScroll = () => {
  if (pendingHashScrollTimer !== null) {
    window.clearTimeout(pendingHashScrollTimer);
    pendingHashScrollTimer = null;
  }
};

const requestLazyAnchorTargets = (hash: string) => {
  window.dispatchEvent(new CustomEvent(ANCHOR_SCROLL_REQUEST_EVENT, { detail: { hash } }));
};

const findHashTarget = (hash: string) => {
  if (!hash || hash === '#') return null;

  const id = decodeURIComponent(hash.slice(1));

  return (
    document.getElementById(id) || document.querySelector<HTMLElement>(`[name="${CSS.escape(id)}"]`)
  );
};

const isTargetAligned = (target: HTMLElement) => {
  const offset = getAnchorScrollOffset();
  const top = target.getBoundingClientRect().top;

  return Math.abs(top - offset) <= ANCHOR_ALIGN_TOLERANCE_PX;
};

const applyInstantScrollTop = (top: number) => {
  window.scrollTo({ top, left: 0, behavior: 'auto' });
};

const getScrollTopForTarget = (target: HTMLElement, fromScrollTop = 0) => {
  const offset = getAnchorScrollOffset();

  return Math.max(0, fromScrollTop + target.getBoundingClientRect().top - offset);
};

const alignHashTargetInstantly = (target: HTMLElement, fromScrollTop = 0) => {
  const top = getScrollTopForTarget(target, fromScrollTop);
  applyInstantScrollTop(top);
  return top;
};

const finishInstantHashScroll = () => {
  requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
};

const interruptAnchorScroll = () => {
  if (
    activeAnchorScrollFrame === null &&
    !anchorScrollInProgress &&
    pendingHashScrollTimer === null
  ) {
    return;
  }

  if (activeAnchorScrollFrame !== null) {
    window.cancelAnimationFrame(activeAnchorScrollFrame);
    activeAnchorScrollFrame = null;
  }
  anchorScrollInProgress = false;
  cancelPendingHashScroll();
};

const scrollToRouteHash = (hash: string, retryCount = 0) => {
  cancelPendingHashScroll();
  activeRouteHash = hash;

  const target = findHashTarget(hash);

  if (!target) {
    requestLazyAnchorTargets(hash);

    if (retryCount < ANCHOR_TARGET_MAX_RETRIES) {
      pendingHashScrollTimer = window.setTimeout(() => {
        scrollToRouteHash(hash, retryCount + 1);
      }, ANCHOR_TARGET_RETRY_DELAY);
      return;
    }

    activeRouteHash = null;
    finishInstantHashScroll();
    return;
  }

  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  alignHashTargetInstantly(target, 0);
  activeRouteHash = null;
  finishInstantHashScroll();
};

const verifyRouteHash = (hash: string) => {
  if (activeRouteHash || anchorScrollInProgress || activeAnchorScrollFrame !== null) return;

  const target = findHashTarget(hash);
  if (!target || isTargetAligned(target)) return;

  alignHashTargetInstantly(target, window.scrollY);
  finishInstantHashScroll();
};

const cancelActiveAnchorScrollFrame = () => {
  if (activeAnchorScrollFrame !== null) {
    window.cancelAnimationFrame(activeAnchorScrollFrame);
    activeAnchorScrollFrame = null;
  }
};

const scheduleHashTargetRetry = (
  hash: string,
  smooth: boolean,
  updateHash: boolean,
  retryCount: number,
) => {
  requestLazyAnchorTargets(hash);

  if (retryCount >= ANCHOR_TARGET_MAX_RETRIES) {
    return false;
  }

  window.setTimeout(() => {
    scrollToHashTarget(hash, smooth, updateHash, retryCount + 1);
  }, ANCHOR_TARGET_RETRY_DELAY);

  return true;
};

const beginAnchorScroll = () => {
  cancelActiveAnchorScrollFrame();
  cancelPendingHashScroll();
  anchorScrollInProgress = true;
  // iOS fires residual touchmove as the finger lifts off a CTA; don't kill
  // the tween we just started from that same tap.
  touchInterruptGraceUntil = performance.now() + TOUCH_INTERRUPT_GRACE_MS;
  touchInterruptStartY = null;
};

const commitUpdatedHash = (hash: string) => {
  if (window.location.hash === hash) return;

  suppressHashChangeScroll = true;
  window.history.replaceState(null, '', hash);
  window.setTimeout(() => {
    suppressHashChangeScroll = false;
  }, 100);
};

const focusAnchorTarget = (target: HTMLElement) => {
  if (!target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
  }
  try {
    target.focus({ preventScroll: true });
  } catch {
    // Older WebKit may lack preventScroll; skip rather than jump the page.
  }
};

const easeInOutCubic = (progress: number) => {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
};

export const scrollToHashTarget = (
  hash: string,
  smooth = true,
  updateHash = false,
  retryCount = 0,
) => {
  const target = findHashTarget(hash);

  if (!target) {
    return scheduleHashTargetRetry(hash, smooth, updateHash, retryCount);
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldAnimate = smooth && !reduceMotion;
  const offset = getAnchorScrollOffset();

  beginAnchorScroll();

  const finish = () => {
    anchorScrollInProgress = false;
    activeAnchorScrollFrame = null;

    if (updateHash) {
      commitUpdatedHash(hash);
    }

    focusAnchorTarget(target);
    window.dispatchEvent(new Event('resize'));
  };

  const current = window.scrollY;
  const top = Math.max(0, current + target.getBoundingClientRect().top - offset);

  if (!shouldAnimate || Math.abs(top - current) < 2) {
    window.scrollTo({ top, left: 0, behavior: 'auto' });
    finish();
    return true;
  }

  const startedAt = performance.now();
  const durationMs = getAnchorScrollDuration(current, top) * 1000;
  const distance = top - current;

  const animateScroll = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / durationMs);
    const eased = easeInOutCubic(progress);

    window.scrollTo({ top: current + distance * eased, left: 0, behavior: 'auto' });

    if (progress < 1 && anchorScrollInProgress) {
      activeAnchorScrollFrame = window.requestAnimationFrame(animateScroll);
      return;
    }

    finish();
  };

  activeAnchorScrollFrame = window.requestAnimationFrame(animateScroll);

  return true;
};

/**
 * Native document scroll + hash-anchor helpers.
 * ScrollSmoother (and its #smooth-wrapper / #smooth-content shells) were
 * removed — they caused 1px hairline gaps between stacked sections.
 */
function isNavigationKey(key: string): boolean {
  return (
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'PageUp' ||
    key === 'PageDown' ||
    key === 'Home' ||
    key === 'End' ||
    key === ' '
  );
}

function handleAnchorClick(event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) {
    return;
  }

  const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
  if (!anchor || anchor.hasAttribute('data-anchor-no-capture')) return;

  const href = anchor.getAttribute('href');
  if (!href || !href.includes('#')) return;

  let url: URL;

  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }

  const isSamePage =
    url.origin === window.location.origin &&
    url.pathname === window.location.pathname &&
    url.search === window.location.search;

  if (!isSamePage || !url.hash) return;

  event.preventDefault();
  cancelPendingHashScroll();
  scrollToHashTarget(url.hash, true, true);
}

export default function GsapSmoothScroller({ children }: GsapSmoothScrollerProps) {
  const pathname = usePathname();

  useLayoutEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    scrollToRouteHash(hash);
  }, [pathname]);

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return undefined;

    const timers = [
      window.setTimeout(() => verifyRouteHash(hash), 120),
      window.setTimeout(() => verifyRouteHash(hash), 400),
      window.setTimeout(() => verifyRouteHash(hash), 900),
    ];

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [pathname]);

  useEffect(() => {
    const onUserScrollIntent = () => {
      interruptAnchorScroll();
    };

    const onTouchStart = (event: TouchEvent) => {
      touchInterruptStartY = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event: TouchEvent) => {
      // A bare touchstart is usually a tap (e.g. Discover Below), not scroll
      // intent. Only cancel the tween once the finger actually moves.
      if (performance.now() < touchInterruptGraceUntil) return;

      const startY = touchInterruptStartY;
      const currentY = event.touches[0]?.clientY;
      if (startY == null || currentY == null) return;

      if (Math.abs(currentY - startY) >= TOUCH_SCROLL_INTERRUPT_THRESHOLD_PX) {
        touchInterruptStartY = null;
        interruptAnchorScroll();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isNavigationKey(event.key)) {
        interruptAnchorScroll();
      }
    };

    // Do not listen to `scroll` — programmatic window.scrollTo during
    // scrollToHashTarget would otherwise interrupt its own animation frame.
    // Do not interrupt on touchstart — on iPhone that fires for every tap
    // and cancels the GSAP tween started by the same tap's click.
    window.addEventListener('wheel', onUserScrollIntent, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    const onClick = handleAnchorClick;

    const onHashChange = () => {
      if (suppressHashChangeScroll) return;

      const hash = window.location.hash;
      if (!hash) return;

      cancelPendingHashScroll();
      scrollToHashTarget(hash, true);
    };

    document.addEventListener('click', onClick, { capture: true });
    window.addEventListener('hashchange', onHashChange);

    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('wheel', onUserScrollIntent);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
      cancelPendingHashScroll();
    };
  }, []);

  return <>{children}</>;
}
